// src/pages/ScorePackagesPage.js
// Updated: 10/31/2025 08:00 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, collection, query, where, onSnapshot, orderBy, getDoc } from 'firebase/firestore'; 
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, FileText } from 'lucide-react'; 

import ScoringForm from '../components/ScoringForm';
import DocumentViewerComponent from '../components/DocumentViewerComponent'; 

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

// --- FIX 1: DEFINE HELPER COMPONENT OUTSIDE MAIN FUNCTION ---
// Renders the list of guidance documents
const GuidanceList = ({ guidanceDocs, isLoading }) => {
    // --- FIX 2: MOVE HOOKS TO TOP LEVEL AND CALL UNCONDITIONALLY ---
    const [docUrls, setDocUrls] = useState({}); 

    useEffect(() => {
        guidanceDocs.forEach(async (guidanceRef) => {
            if (!docUrls[guidanceRef.documentId]) {
                try {
                    const docSnap = await getDoc(doc(db, 'documents', guidanceRef.documentId));
                    if (docSnap.exists()) {
                        setDocUrls(prev => ({ 
                            ...prev, 
                            [guidanceRef.documentId]: docSnap.data().downloadURL 
                        }));
                    }
                } catch(e) {
                    console.error("Failed to fetch guidance document URL:", e);
                }
            }
        });
    }, [guidanceDocs, docUrls]);
    // --- END HOOK FIX ---


    if (isLoading) {
        return (
            <div className="mb-6 rounded-lg bg-gray-50 p-4 shadow-inner">
                <h3 className="mb-3 text-lg font-semibold text-gray-700 border-b pb-2">
                    Session Guidance
                </h3>
                <LoadingSpinner />
            </div>
        );
    }
    if (guidanceDocs.length === 0) return null; // Don't render if there are no docs

    return (
        <div className="mb-6 rounded-lg bg-gray-50 p-4 shadow-inner">
            <h3 className="mb-3 text-lg font-semibold text-gray-700 border-b pb-2">
                Session Guidance
            </h3>
            <ul className="space-y-2">
                {guidanceDocs.map(docRef => {
                    const url = docUrls[docRef.documentId];
                    return (
                        <li key={docRef.id}>
                            <a
                                href={url || '#'} 
                                target="_blank" // Opens in new tab
                                rel="noopener noreferrer"
                                onClick={(e) => { 
                                    if (!url) e.preventDefault();
                                }}
                                className={`flex items-center text-sm font-medium ${url ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-wait'}`}
                            >
                                <FileText className="h-4 w-4 mr-2" />
                                {docRef.title}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
// --- END HELPER COMPONENT DEFINITION ---


export default function ScorePackagesPage() {
  const { sessionId } = useParams();
  const { currentUser, userData } = useAuth();
  const [session, setSession] = useState(null);
  const [packages, setPackages] = useState([]);
  const [guidanceDocs, setGuidanceDocs] = useState([]); 
  const [scores, setScores] = useState({});
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingScores, setIsLoadingScores] = useState(true);
  const [isLoadingGuidance, setIsLoadingGuidance] = useState(true); 
  const [error, setError] = useState('');

  // 1. Fetch Scoring Session Details
  useEffect(() => {
    if (!sessionId) return;
    setIsLoadingSession(true);
    const sessionRef = doc(db, 'scoringSessions', sessionId);
    const unsubscribe = onSnapshot(sessionRef, (docSnap) => {
      if (docSnap.exists()) {
        const sessionData = { id: docSnap.id, ...docSnap.data() };
        if (userData?.orgId && userData.orgId !== sessionData.orgId) {
            setError('Access Denied: Session belongs to another organization.'); setSession(null);
        } else if (sessionData.status !== 'open') {
             setError('This scoring session is not currently open.'); setSession(sessionData);
        } else if (!sessionData.reviewers?.includes(currentUser?.uid)) {
             setError('You are not assigned as a reviewer for this session.'); setSession(sessionData);
        } else {
            setSession(sessionData); setError('');
        }
      } else { setError('Scoring session not found.'); setSession(null); }
      setIsLoadingSession(false);
    }, (err) => { console.error("Error fetching session:", err); setError("Failed to load session details."); setIsLoadingSession(false); });
    return () => unsubscribe();
  }, [sessionId, userData?.orgId, currentUser?.uid]);

  // 2. Fetch Packages for this Session
  useEffect(() => {
    if (!sessionId) return;
    setIsLoadingPackages(true);
    const packagesQuery = query(collection(db, 'scoringSessions', sessionId, 'packages'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(packagesQuery, (querySnapshot) => {
      const pkgs = [];
      querySnapshot.forEach((doc) => pkgs.push({ id: doc.id, ...doc.data() }));
      setPackages(pkgs);
      if (!selectedPackageId && pkgs.length > 0) {
        setSelectedPackageId(pkgs[0].id);
      }
      setIsLoadingPackages(false);
    }, (err) => {
      console.error("Error fetching packages:", err); setError(prev => prev + " Failed to load packages."); setIsLoadingPackages(false);
    });
    return () => unsubscribe();
  }, [sessionId, selectedPackageId]);

  // 3. Fetch *this user's* existing scores for *this session*
  useEffect(() => {
      if (!sessionId || !currentUser?.uid) return;
      setIsLoadingScores(true);
      const scoresQuery = query(
          collection(db, 'scoringSessions', sessionId, 'scores'),
          where('reviewerId', '==', currentUser.uid)
      );
      const unsubscribe = onSnapshot(scoresQuery, (querySnapshot) => {
          const userScores = {};
          querySnapshot.forEach((doc) => {
              const scoreData = { id: doc.id, ...doc.data() }; userScores[scoreData.packageId] = scoreData;
          });
          setScores(userScores); setIsLoadingScores(false);
      }, (err) => { console.error("Error fetching user scores:", err); setError(prev => prev + " Failed to load your scores."); setIsLoadingScores(false); });
      return () => unsubscribe();
  }, [sessionId, currentUser?.uid]);

  // 4. Fetch Guidance Documents
  useEffect(() => {
    if (!sessionId) return;
    setIsLoadingGuidance(true);
    const guidanceQuery = query(collection(db, 'scoringSessions', sessionId, 'guidanceDocs'), orderBy('uploadedAt', 'desc'));
    const unsubscribe = onSnapshot(guidanceQuery, (querySnapshot) => {
        const docs = [];
        querySnapshot.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
        setGuidanceDocs(docs);
        setIsLoadingGuidance(false);
    }, (err) => { 
        console.error("Error fetching guidance documents:", err); 
        setError(prev => prev + " Failed to load guidance documents."); 
        setIsLoadingGuidance(false);
    });
    return () => unsubscribe();
  }, [sessionId]);


  const isLoading = isLoadingSession || isLoadingPackages || isLoadingScores || isLoadingGuidance;
  const selectedPackage = packages.find(p => p.id === selectedPackageId);
  const userScoreForSelected = scores[selectedPackageId];

  if (isLoadingSession) return <LoadingSpinner />;
  if (error && !session) return <p className="text-red-600">{error}</p>;
  if (!session) return <p>Session not found.</p>;


  // Determine if the user can score based on the loaded session data
  const canScore = session?.status === 'open' && session?.reviewers?.includes(currentUser?.uid);

  return (
    <div>
      {/* Back Link */}
      <Link to="/scoring" className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Scoring Sessions
      </Link>

      {/* Session Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{session.name}</h1>
        <p className="mt-1 text-gray-600">{session.description || 'Score the assigned packages.'}</p>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>

      {/* Main Layout: Package List (Left) + Split-View (Right) */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">

        {/* 1. Package List Sidebar */}
        <div className="md:col-span-1 lg:col-span-1">
          <div className="rounded-lg bg-white p-4 shadow">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">Packages ({packages.length})</h2>
            {isLoadingPackages ? <LoadingSpinner /> : (
              <nav className="flex flex-col space-y-1">
                {packages.map(pkg => {
                  const hasScore = scores[pkg.id];
                  return (
                    <button key={pkg.id} onClick={() => setSelectedPackageId(pkg.id)} className={`w-full rounded px-3 py-2 text-left text-sm font-medium transition ${selectedPackageId === pkg.id ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} >
                      {pkg.name} {hasScore && '✓'}
                    </button>
                  );
                 })}
              </nav>
            )}
          </div>
        </div>

        {/* 2. Split-View Scoring Area (2-column inside the main area) */}
        <div className="md:col-span-2 lg:col-span-3">
          {isLoading ? (
             <LoadingSpinner />
          ) : !selectedPackage ? (
              <div className="rounded-lg bg-white p-6 shadow">
                  <GuidanceList guidanceDocs={guidanceDocs} isLoading={isLoadingGuidance} />
                  <p className="text-center text-gray-500">{packages.length > 0 ? 'Select a package to start scoring.' : 'No packages found for this session.'}</p>
               </div>
          ) : !canScore ? (
                <div className="rounded-lg bg-white p-6 shadow">
                     <p className="text-center text-red-600">{error || 'Scoring is not available.'}</p>
                </div>
          ) : (
            <div className="space-y-6">
                
                {/* A. Guidance Documents List */}
                <GuidanceList guidanceDocs={guidanceDocs} isLoading={isLoadingGuidance} />

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    
                    {/* B. Document Review/Notes Section (Left Side of Split) */}
                    <div className="xl:col-span-1">
                        <div className="rounded-lg bg-white p-6 shadow-lg h-full">
                            <h3 className="mb-4 text-xl font-semibold text-gray-800 border-b pb-2">
                                Review: {selectedPackage.name}
                            </h3>
                            <div className="h-[65vh] overflow-y-auto border p-2 bg-gray-50">
                                <DocumentViewerComponent 
                                    documentId={selectedPackage.linkedDocumentId} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* C. Scoring Form Section (Right Side of Split) */}
                    <div className="xl:col-span-1">
                        <div className="h-[65vh] overflow-y-auto">
                            <ScoringForm
                                session={session}
                                packageData={selectedPackage}
                                existingScore={userScoreForSelected}
                            />
                        </div>
                    </div>
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}